import { computed, ref } from 'vue' 
import Moment from 'moment'
import { useLocalizationStore } from '@/store/localization';
import { useUnitStore } from '@/store/unit';
import { useClassificationStore } from '@/store/classification';
import { IMaterialDTO } from '@/types/material';
import { Material } from '@/util'
import { useUserStore } from '@/store/user';


export function buildNumberFormatter(options?: Intl.NumberFormatOptions) {
    const userLocale = navigator.language
    const numberFormatter = new Intl.NumberFormat(userLocale, options)
    return (n: number) => isNaN(n) ? '' : numberFormatter.format(n)
}

export function builDecimalNumberFormatter(decimals: number) {
    return buildNumberFormatter({ style: 'decimal', minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export function buildDateFormatter() {
    const locale = navigator.language
    const toLocal = (d: Date | string) => Moment.utc(d).local().locale(locale)
    const asLocal = (d: Date | string) => Moment(d).locale(locale)

    const formatDate = (d: Date | null | undefined): string =>
        d == null ? '' : asLocal(d).format('L')

    const formatUtcDate = (d: Date | string | null | undefined): string =>
        d == null ? '' : toLocal(d).format('L')

    const formatDateTime = (d: Date | null | undefined): string =>
        d == null ? '' : `${asLocal(d).format('L')} ${asLocal(d).format('LT')}`

    const formatUtcDateTime = (d: Date | string | null | undefined): string =>
        d == null ? '' : `${toLocal(d).format('L')} ${toLocal(d).format('LT')}`

    const formatTime = (d: Date | null | undefined): string =>
        d == null ? '' : asLocal(d).format('LT')

    const formatUtcTime = (d: Date | string | null | undefined): string =>
        d == null ? '' : toLocal(d).format('LT')

    return { formatDate, formatUtcDate, formatDateTime, formatUtcDateTime, formatTime, formatUtcTime }
}

export function buildLocalization() {
    const localizationStore = ref(useLocalizationStore());
    const localize = (id: string) => localizationStore.value.get(id);
    const currentLanguage = computed(() => localizationStore.value.language)

    return { localizationStore, localize, currentLanguage }
}

export function buildUnitLocalization() {
    const unitStore = ref(useUnitStore());
    const localizeUnit3 = (unit?: string) => {
        if (unit == null) return '';
        return unitStore.value.get(unit)?.text3?.toLocaleLowerCase() ?? '';
    }

    return { unitStore, localizeUnit3 }
}

export function buildClassificationLocalization() {
    const propertyValueDict = ref(useClassificationStore());
    const classificationHasLoaded = computed(() => propertyValueDict.value.hasLoaded);

    const getPropValueDTO = (propName: string, propValue?: string | null) =>
        propertyValueDict.value.get(propName, propValue);

    const localizePropValue = (propName: string, propValue?: string | null) =>
        propertyValueDict.value.get(propName, propValue)?.propertyValueText ?? '';

    const localizePropValueHierarchy = (propName: string, propValue?: string | null) =>
        propertyValueDict.value.getHierarchyTexts(propName, propValue);


    return { propertyValueDict, getPropValueDTO, localizePropValue, localizePropValueHierarchy, classificationHasLoaded };
}

export function buildMaterialLocalization() {
    const { localizationStore, localize } = buildLocalization()
    const currentLanguage = computed(() => localizationStore.value.language)

    function materialTextFor(material?: IMaterialDTO): string {
        if (material == null) return '';

        const m = new Material(material)
        return m.shortText(currentLanguage.value)
    }

    function materialSalesTextFor(material?: IMaterialDTO): string {
        if (material == null) return '';

        const m = new Material(material)
        return m.salesText(currentLanguage.value)
    }

    return { localizationStore, localize, currentLanguage, materialTextFor, materialSalesTextFor }
}

export function buildBomItemLocalization() {
    const userStore = useUserStore()
    const { localize } = buildLocalization()
    const isAdmin = computed(() => userStore.isAdmin)

    function localizeBomItemText(bomItemType: string): string {
        let itemText = localize(bomItemType)

        if (isAdmin.value) {
            let bomUsage: string = ''

            switch (bomItemType.toLowerCase()) {
                // resx usage: localize('bomItems_alternatives')
                case 'bomitems_alternatives':
                    bomUsage = '8'
                    break

                // resx usage: localize('bomitems_maintenance')
                case 'bomitems_maintenance':
                    bomUsage = '4'
                    break

                // resx usage: localize('bomitems_production')
                case 'bomitems_production':
                    bomUsage = '3'
                    break

                // resx usage: localize('bomitems_recommended')
                case 'bomitems_recommended':
                    bomUsage = '5'
                    break

                // resx usage: localize('bomitems_related')
                case 'bomitems_related':
                    bomUsage = '3+4'
                    break

                default:
                    bomUsage = '?'
                    break
            }

            itemText = `${itemText} (${localize('bomUsage')} ${bomUsage})`
        }

        return itemText
    }

    return { localizeBomItemText }
}